import { NgxLoadingButtonsModule } from 'ngx-loading-buttons';

import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, ModuleWithProviders, Optional, SkipSelf } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';

import { SnAccountModule } from '@lib/account';
import { ExternallyManagedGuard } from '@lib/auth/guards/externally-managed.guard';
import { throwIfAlreadyLoaded } from '@lib/common/helpers';
import { SnFormModule } from '@lib/form';
import { SnFormCheckboxModule } from '@lib/form/checkbox';
import { SnFormInputModule } from '@lib/form/input';
import { SnFormMobilePhoneModule } from '@lib/form/mobile-phone';
import { SnFormPlacesAutocompleteModule } from '@lib/form/places-autocomplete';
import { SnFormRadioModule } from '@lib/form/radio';
import { SnFormRecaptchaModule } from '@lib/form/recaptcha';
import { SnGoogleGeocoderService } from '@lib/geo/google/geocoder';
import { TranslationModule } from '@lib/translation';
import { SnAlertModule } from '@lib/ui/alert';
import { SnBrandModule } from '@lib/ui/brand';
import { SnButtonModule } from '@lib/ui/button';
import { SnIconModule } from '@lib/ui/icon';

import { AuthRoutingModule } from './auth-routing.module';
import { ResetConfirmFormComponent } from './components/reset-confirm-form';
import { ResetPasswordFormComponent } from './components/reset-password-form';
import { SignInFormComponent } from './components/sign-in-form';
import { TwoFactorAuthDialogComponent } from './components/two-factor-auth-dialog/two-factor-auth-dialog.component';
// containers
import { SnActivateContainer } from './containers/activate';
import { ConfirmEmailContainer } from './containers/confirm-email';
import { ConfirmPhoneContainer } from './containers/confirm-phone';
import { CreatePasswordContainer } from './containers/create-password';
import {
  SnAuthorizedGuard,
  SnUnauthorizedGuard,
  SignUpEnabledGuard,
  SsoEnabledGuard,
} from './guards';
import { SnApiTokenInterceptor } from './interceptors';
// routing
import { ISnAuthConfig } from './interfaces';
import { SN_AUTH_CONFIG, authConfigFactory } from './providers';
import { ActivateView } from './views/activate';
// views
import { AuthView } from './views/auth';
import { AxonDisclaimerComponent } from './views/axon-disclaimer';
import { ChangePasswordComponent } from './views/change-password/change-password.component';
import { CjisComplianceComponent } from './views/cjis-compliance/cjis-compliance.component';
import { EcomAssociationComponent } from './views/ecom-association/ecom-association.component';
import { MouDialogComponent } from './views/mou-dialog/mou-dialog.component';
import { ResetConfirmView } from './views/reset-confirm';
import { ResetPasswordView } from './views/reset-password';
import { SignInView } from './views/sign-in';
import { SingleSignOnView } from './views/single-sign-on/single-sign-on.view';
import { SsoDeniedView } from './views/sso-denied/sso-denied.view';
import { SsoErrorView } from './views/sso-error/sso-error.view';
import { SsoMobileDeniedComponent } from './views/sso-mobile-denied/sso-mobile-denied.component';
import { SsoMobileSamlComponent } from './views/sso-mobile-saml/sso-mobile-saml.component';
import { SsoMobileSuccessComponent } from './views/sso-mobile-success/sso-mobile-success.component';
import { SsoSuccessView } from './views/sso-success/sso-success.view';
import { WarningDialogComponent } from './views/warning-dialog/warning-dialog.component';

// components

@NgModule({
  imports: [
    // Angular
    CommonModule,
    // Material
    MatStepperModule,
    MatProgressBarModule,
    MatButtonModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    // Libs
    SnAccountModule,
    SnIconModule,
    SnBrandModule,
    SnAlertModule,
    SnButtonModule,
    SnFormModule,
    SnFormInputModule,
    SnFormRecaptchaModule,
    SnFormMobilePhoneModule,
    SnFormRadioModule,
    SnFormPlacesAutocompleteModule,
    SnFormCheckboxModule,
    NgxLoadingButtonsModule,
    TranslationModule,
    // Own
    AuthRoutingModule,
    // Standalone components
    SsoSuccessView,
  ],
  declarations: [
    // Components
    SignInFormComponent,
    MouDialogComponent,
    WarningDialogComponent,
    ResetConfirmFormComponent,
    ResetPasswordFormComponent,
    // Containers
    SnActivateContainer,
    ConfirmEmailContainer,
    ConfirmPhoneContainer,
    CreatePasswordContainer,
    // views
    AuthView,
    SignInView,
    ActivateView,
    ResetConfirmView,
    ResetPasswordView,
    CjisComplianceComponent,
    EcomAssociationComponent,
    SingleSignOnView,
    SsoDeniedView,
    SsoErrorView,
    SsoMobileSamlComponent,
    SsoMobileSuccessComponent,
    SsoMobileDeniedComponent,
    AxonDisclaimerComponent,
    ChangePasswordComponent,
    TwoFactorAuthDialogComponent,
  ],
})
export class SnAuthModule {
  public static forRoot(config?: Partial<ISnAuthConfig>): ModuleWithProviders<SnAuthModule> {
    return {
      ngModule: SnAuthModule,
      providers: [
        SnAuthorizedGuard,
        SnUnauthorizedGuard,
        SignUpEnabledGuard,
        SsoEnabledGuard,
        ExternallyManagedGuard,
        SnGoogleGeocoderService,
        {
          provide: SN_AUTH_CONFIG,
          useValue: authConfigFactory(config),
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: SnApiTokenInterceptor,
          multi: true,
        },
      ],
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: SnAuthModule) {
    throwIfAlreadyLoaded(parentModule, 'SnAuthModule');
  }
}
